// Reports.jsx — CSV export + governance summary
import React, { useEffect, useState } from "react";
import {
  Box, Paper, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, MenuItem, TextField,
} from "@mui/material";
import { Download, FileDown, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { riskApi, API_BASE } from "../api/api";
import { fmtINR, fmtScore, fmtBps, fmtPct, HAZARD_LABELS } from "../utils/format";

export default function Reports() {
  const [scenario, setScenario] = useState("current");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPortfolio, setFilterPortfolio] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await riskApi.cachedPortfolio(scenario, filterPortfolio || undefined);
      setData(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scenario, filterPortfolio]);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_BASE}/reports/portfolio.csv?scenario=${scenario}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `climate_portfolio_${scenario}.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error("Download failed"); }
  };

  const s = data?.summary || {};
  const byIndustry = data?.by_industry || [];
  const byState = data?.by_state || [];

  return (
    <DashboardLayout
      title="Reports & Governance"
      subtitle="EXPORTS · DISCLOSURES · TCFD-ALIGNED"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button variant="contained" size="small" startIcon={<FileDown size={13} />}
            onClick={downloadCsv}
            data-testid="export-csv-btn"
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            Export CSV
          </Button>
        </Box>
      }
    >
      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 1.4, alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, color: "#2E5A35", fontWeight: 600 }}>
          <ShieldCheck size={14} /> Governance snapshot
        </Box>
        <TextField size="small" select label="Portfolio" value={filterPortfolio}
          onChange={(e) => setFilterPortfolio(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="lending">Lending</MenuItem>
          <MenuItem value="investment">Investment</MenuItem>
        </TextField>
      </Paper>

      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box> : (
        <>
          {/* Summary KPIs */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.6, mb: 2 }}>
            <Stat label="Collaterals scored" value={s.collateral_count || 0} mono />
            <Stat label="Avg score" value={fmtScore(s.avg_score)} mono />
            <Stat label="Total exposure" value={fmtINR(s.total_financial_exposure_inr)} mono />
            <Stat label="Avg PD uplift" value={fmtBps(s.avg_pd_uplift_bps)} mono accent="#8F5E14" />
            <Stat label="High-risk %" value={fmtPct(s.high_risk_pct)} mono accent="#A93226" />
            <Stat label="Critical" value={s.level_counts?.Critical || 0} mono accent="#4A1A1A" />
            <Stat label="High" value={s.level_counts?.High || 0} mono accent="#A93226" />
            <Stat label="Low" value={s.level_counts?.Low || 0} mono accent="#2E5A35" />
          </Box>

          {/* By Industry & By State */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <Paper sx={{ overflow: "hidden" }} data-testid="rep-by-industry">
              <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
                <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600 }}>By Sector</Box>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sector</TableCell>
                    <TableCell align="right">Assets</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Exposure</TableCell>
                    <TableCell align="right">Hi-risk</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byIndustry.map((r) => (
                    <TableRow key={r.industry}>
                      <TableCell sx={{ fontWeight: 600, textTransform: "capitalize" }}>{r.industry?.replace("_", " ")}</TableCell>
                      <TableCell align="right" className="cb-mono">{r.count}</TableCell>
                      <TableCell align="right" className="cb-mono">{fmtScore(r.avg_score)}</TableCell>
                      <TableCell align="right" className="cb-mono">{fmtINR(r.fin_sum)}</TableCell>
                      <TableCell align="right" className="cb-mono">{r.high}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Paper sx={{ overflow: "hidden" }} data-testid="rep-by-state">
              <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
                <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600 }}>By State</Box>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Assets</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Exposure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byState.map((r) => (
                    <TableRow key={r.state}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.state}</TableCell>
                      <TableCell align="right" className="cb-mono">{r.count}</TableCell>
                      <TableCell align="right" className="cb-mono">{fmtScore(r.avg_score)}</TableCell>
                      <TableCell align="right" className="cb-mono">{fmtINR(r.fin_sum)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>

          {/* Hazard table */}
          <Paper sx={{ mt: 2, overflow: "hidden" }} data-testid="rep-hazard">
            <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
              <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600 }}>Hazard Exposure</Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hazard</TableCell>
                  <TableCell align="right">Collaterals</TableCell>
                  <TableCell align="right">High/Critical</TableCell>
                  <TableCell align="right">Avg score</TableCell>
                  <TableCell align="right">Exposure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(s.hazard_breakdown || []).map((h) => (
                  <TableRow key={h.hazard_id}>
                    <TableCell sx={{ fontWeight: 600 }}>{HAZARD_LABELS[h.hazard_id] || h.hazard_name}</TableCell>
                    <TableCell align="right" className="cb-mono">{h.collateral_count}</TableCell>
                    <TableCell align="right" className="cb-mono">{h.high_risk_collateral_count}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtScore(h.avg_score)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(h.financial_exposure_inr)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, mono, accent = "#1E2A38" }) {
  return (
    <Paper sx={{ p: 2, borderLeft: `3px solid ${accent}` }}>
      <Box className="cb-kpi-label">{label}</Box>
      <Box className={mono ? "cb-mono" : "cb-sans"} sx={{ fontSize: 22, fontWeight: 600, color: "#111827", mt: 0.3 }}>
        {value}
      </Box>
    </Paper>
  );
}
